// state
import gql from 'graphql-tag';
import { CORE_DASHBOARD_FIELDS } from '~/graphql/fragments';
import { apolloClient } from '~/plugins/apollo';

export const state = {
  // The user dashboards
  userDashboards: [],
  // All dashboards
  dashboards: [],
  // The dashboard of the route, fetched by its id: the client-specific dashboards are never listed
  currentDashboard: null,
  widgetBeingExported: null,
  widgetConfigurationBeingExported: null,
  // Shared "include planned stops" state for the global-losses modules: the
  // waterfall owns the toggle, the production-rate module reads it (KP-18653).
  globalLossesPlannedStops: false,
};

// getters
export const getters = {
  getUserDashboards: (state) => state.userDashboards,
  getDashboards: (state) => state.dashboards,
  /**
   * Find a dashboard by its id among all the dashboards, then the current dashboard
   * (fetched by its id: the client-specific dashboards are never listed)
   * @returns {function(string|number): (Object|undefined)}
   */
  getDashboard: (state) => (id) => [...state.dashboards, state.currentDashboard]
    .find((dashboard) => dashboard && `${dashboard.id}` === `${id}`),
};

// mutations
export const mutations = {
  commitUserDashboards(state, data) {
    state.userDashboards = data;
  },
  commitDashboards(state, data) {
    state.dashboards = data;
  },
  commitCurrentDashboard(state, data) {
    state.currentDashboard = data;
  },
  commitWidgetBeingExported(state, data) {
    state.widgetBeingExported = data;
  },
  commitWidgetConfigurationBeingExported(state, data) {
    state.widgetConfigurationBeingExported = data;
  },
  commitGlobalLossesPlannedStops(state, data) {
    state.globalLossesPlannedStops = data;
  },
};

// actions
export const actions = {
  /**
   * Reset the state
   *
   * @param context
   * @returns {Promise<void>}
   */
  async reset(context) {
    context.commit('commitUserDashboards', []);
    context.commit('commitCurrentDashboard', null);
  },
  /**
   * Fetch all dashboards
   *
   * @param context
   * @returns {Promise<any>}
   */
  async fetchDashboards(context) {
    const dashboardsData = (await apolloClient.query(
      {
        query: gql`
          query AllDashboards {
            dashboards(first: 200, page:1) {
              data {
                ...CoreDashboardFields
              }
            }
          }
          ${CORE_DASHBOARD_FIELDS}
        `,
      },
    )).data;

    if (dashboardsData?.dashboards?.data) {
      context.commit('commitDashboards', dashboardsData.dashboards.data);
      return dashboardsData.dashboards.data;
    }

    context.commit('commitDashboards', []);
    return null;
  },

  async fetchUserDashboards(context) {
    const { data } = (await apolloClient.query({
      query: gql`
          query me {
            me {
              dashboardsExpand {
                ...CoreDashboardFields
              }
            }
          }
          ${CORE_DASHBOARD_FIELDS}
        `,
    }));
    context.commit('commitUserDashboards', data?.me?.dashboardsExpand);
  },

};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
